/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Card,
  Spin,
} from "antd";
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import ContainerLayout from "../../common/containerLayout";
import DashboardChart from '../DashboardChart';
import { MetricDataset } from '../DashboardChart';
import { getCompaniesWithInteractionsReport, getSalesReport, getTripsReport, getUsersReport, getAnalyticsConversionTrend } from '../../../services/ReportsApi';

dayjs.extend(weekOfYear);

const { RangePicker } = DatePicker;
const { Option } = Select;

export interface DateRange {
  startDate: Dayjs;
  endDate: Dayjs;
}

export type PeriodType = 'daily' | 'weekly' | 'monthly';

const DashboardView: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<undefined | {id: number, name: string}>();
  const [period, setPeriod] = useState<PeriodType>('monthly');
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: dayjs().subtract(6, 'month'),
    endDate: dayjs()
  });
  const [dateLimits] = useState({
    maxDays: 60,
    maxWeeks: 52,
    maxMonths: 36
  });

  const [companiesWithInteration, setCompaniesWithInteration] = useState<{id: number, name: string}[]>([]);
  const [salesData, setSalesData] = useState<MetricDataset[]>([]);
  const [usersData, setUsersData] = useState<MetricDataset[]>([]);
  const [tripsData, setTripsData] = useState<MetricDataset[]>([]);
  const [conversionData, setConversionData] = useState<MetricDataset[]>([]);

  const getDefaultDateRange = (periodType: PeriodType): DateRange => {
    const endDate = dayjs();
    switch (periodType) {
      case 'daily':
        return {
          startDate: endDate.subtract(2, 'month'),
          endDate: endDate
        };
      case 'weekly':
        return {
          startDate: endDate.subtract(1, 'year'),
          endDate: endDate
        };
      case 'monthly':
        return {
          startDate: endDate.subtract(3, 'year'),
          endDate: endDate
        };
      default:
        return {
          startDate: endDate.subtract(2, 'month'),
          endDate: endDate
        };
    }
  };

  const validateAndAdjustDateRange = (startDate: Dayjs, endDate: Dayjs, periodType: PeriodType): DateRange => {
    const diffInDays = endDate.diff(startDate, 'day');
    const diffInMonths = endDate.diff(startDate, 'month');
    
    switch (periodType) {
      case 'daily':
        if (diffInDays > dateLimits.maxDays) {
          return {
            startDate: endDate.subtract(dateLimits.maxDays, 'day'),
            endDate: endDate
          };
        }
        break;
      case 'weekly':
        if (diffInMonths > 12) {
          return {
            startDate: endDate.subtract(1, 'year'),
            endDate: endDate
          };
        }
        break;
      case 'monthly':
        if (diffInMonths > dateLimits.maxMonths) {
          return {
            startDate: endDate.subtract(dateLimits.maxMonths, 'month'),
            endDate: endDate
          };
        }
        break;
    }
    
    return { startDate, endDate };
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    if (current && current < dayjs('2025-01-01').startOf('day')) {
      return true;
    }

    const today = dayjs().endOf('day');
    
    switch (period) {
      case 'daily':
        const twoMonthsAgo = today.subtract(dateLimits.maxDays, 'day');
        return current && (current > today || current < twoMonthsAgo);
      
      case 'weekly':
        const oneYearAgo = today.subtract(1, 'year');
        return current && (current > today || current < oneYearAgo);
      
      case 'monthly':
        const threeYearsAgo = today.subtract(dateLimits.maxMonths, 'month');
        return current && (current > today || current < threeYearsAgo);
      
      default:
        return false;
    }
  };

  const getSalesData = async () => {
    try {
      let sales: MetricDataset[] = [
        {
          name: 'Ventas Totales',
          data: [],
          color: '#1976d2'
        },
        {
          name: 'Driver comission',
          data: [],
          color: '#388e3c'
        },
        {
          name: 'Mowi comission',
          data: [],
          color: '#ed6c02'
        }
      ]
      const result = await getSalesReport(
        dateRange.startDate.format('YYYY-MM-DD'), 
        dateRange.endDate.format('YYYY-MM-DD'), 
        period,
        selectedCompanyId === 'all' ? '' : selectedCompanyId,
      );
      for (let i = 0; i < result.length; i++) {
        sales[0].data.push({date: result[i].period, value: result[i].totalSales});
        sales[1].data.push({date: result[i].period, value: result[i].driverCommissions});
        sales[2].data.push({date: result[i].period, value: (result[i].totalSales - result[i].driverCommissions)});
        
      }
      setSalesData(sales);
    } catch (error) {
    }
  };

  const getUsersData = async () => {
    try {
      let users: MetricDataset[] = [
        {
          name: 'Nuevos usuarios',
          data: [],
          color: '#1976d2'
        },
        {
          name: 'Nuevos usuarios con viajes',
          data: [],
          color: '#388e3c'
        },
      ]
      const result = await getUsersReport(
        dateRange.startDate.format('YYYY-MM-DD'), 
        dateRange.endDate.format('YYYY-MM-DD'), 
        period,
        selectedCompanyId === 'all' ? '' : selectedCompanyId,
      );
      for (let i = 0; i < result.length; i++) {
        users[0].data.push({date: result[i].period, value: result[i].newUsers});
        users[1].data.push({date: result[i].period, value: result[i].newUsersWithTrip});
      }
      setUsersData(users);
    } catch (error) {
    }
  };

  const getTripsData = async () => {
    try {
      let trips: MetricDataset[] = [
        {
          name: 'Nuevos viajes',
          data: [],
          color: '#1976d2'
        },
        {
          name: 'Directos',
          data: [],
          color: '#cccc66'
        },
        {
          name: 'Agendados',
          data: [],
          color: '#ed6c02'
        },
        {
          name: 'Completados',
          data: [],
          color: '#2e7d32'
        },
        {
          name: 'Cancelados',
          data: [],
          color: '#d32f2f'
        },
      ]

      const result = await getTripsReport(
        dateRange.startDate.format('YYYY-MM-DD'), 
        dateRange.endDate.format('YYYY-MM-DD'), 
        period,
        selectedCompanyId === 'all' ? '' : selectedCompanyId,
      );
      for (let i = 0; i < result.length; i++) {
        trips[0].data.push({date: result[i].period, value: result[i].newTrips});
        trips[1].data.push({date: result[i].period, value: result[i].directTrips});
        trips[2].data.push({date: result[i].period, value: result[i].scheduledTrips});
        trips[3].data.push({date: result[i].period, value: result[i].completedTrips});
        trips[4].data.push({date: result[i].period, value: result[i].cancelledTrips});
      }
      setTripsData(trips);
    } catch (error) {
    }
  };

  const getConversionData = async () => {
    try {
      const result = await getAnalyticsConversionTrend(
        dateRange.startDate.format('YYYY-MM-DD'),
        dateRange.endDate.format('YYYY-MM-DD'),
        period,
      );
      const dataset: MetricDataset = {
        name: 'Tasa de Conversión (%)',
        data: (result || []).map((r: any) => ({
          date: r.period,
          value: r.conversionRate,
        })),
        color: '#9c27b0',
      };
      setConversionData([dataset]);
    } catch (error) {
      setConversionData([]);
    }
  };

  const getData = async () => {
    setLoading(true);
    const promises = [
      getSalesData(),
      getUsersData(),
      getTripsData(),
      getConversionData(),
    ];
    await Promise.allSettled(promises);
    setLoading(false);
  }

  useEffect(() => {
    if (loading) {
      return;
    }
    getData();
  }, [dateRange, period, selectedCompanyId]);

  useEffect(() => {
    if (loadingCompanies) {
      return;
    }
    setLoadingCompanies(true)
    getCompaniesWithInteractionsReport(
      dateRange.startDate.format('YYYY-MM-DD'), 
      dateRange.endDate.format('YYYY-MM-DD'),
    ).then(r => {
      setCompaniesWithInteration(r)
    }).finally(() => setLoadingCompanies(false));
  }, [dateRange]);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      const adjustedRange = validateAndAdjustDateRange(dates[0], dates[1], period);
      setDateRange(adjustedRange);
      
      if (!adjustedRange.startDate.isSame(dates[0]) || !adjustedRange.endDate.isSame(dates[1])) {
        form.setFieldValue('dateRange', [adjustedRange.startDate, adjustedRange.endDate]);
      }
    }
  };

  const handleCompanyChange = (value: string) => {
    setSelectedCompanyId(value);
    setSelectedCompany(companiesWithInteration.find(c => `${c.id}` === value));
    form.setFieldsValue({
      company: value,
    });
  };

  const handlePeriodChange = (value: PeriodType) => {
    setPeriod(value);
    
    const newDefaultRange = getDefaultDateRange(value);
    setDateRange(newDefaultRange);
    
    form.setFieldsValue({
      period: value,
      dateRange: [newDefaultRange.startDate, newDefaultRange.endDate]
    });
  };

  const chartConfigs = useMemo(() => [
    {
      title: `Ventas${selectedCompany?.name ? ` - ${selectedCompany.name}` : ''}`, 
      datasets: salesData,
      key: 'sales',
    },
    {
      title: `Nuevos Usuarios${selectedCompany?.name ? ` - ${selectedCompany.name}` : ''}`, 
      datasets: usersData,
      key: 'users'
    },
    {
      title: `Viajes${selectedCompany?.name ? ` - ${selectedCompany.name}` : ''}`, 
      datasets: tripsData,
      key: 'trips'
    },
    {
      title: 'Viajes Internacionales',
      datasets: conversionData,
      key: 'conversion',
    },
  ].filter(config => config.datasets.length > 0), [salesData, usersData, tripsData, conversionData, selectedCompany]);

  const getGridColumns = () => {
    const chartCount = chartConfigs.length;
    
    if (chartCount === 0 || chartCount === 1) return 1;
    if (chartCount === 2 || chartCount === 4) return 2;
    if (chartCount === 3 || chartCount >= 5) return 3;
    
    return 2;
  };

  const gridColumns = getGridColumns();

  return (
    <ContainerLayout title="Dashboard de Métricas" showNotification={false}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          period: period,
          dateRange: [dateRange.startDate, dateRange.endDate],
          company: 'all',
        }}
        style={{
          width: '100%',
          padding: '0 1em',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        
        <Card style={{ marginBottom: '1em', padding: '1em 1em 0 1em' }} bodyStyle={{padding: '0'}}>
          <Row gutter={16} align="middle">
            <Col xs={24} sm={8}>
              <Form.Item label="Empresa" name="company">
                <Select onChange={handleCompanyChange} >
                  <Option value="all">Todos</Option>
                  {companiesWithInteration.map(c =>
                    <Option key={`${c.id}`} value={`${c.id}`}>{c.name}</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Periodo" name="period">
                <Select onChange={handlePeriodChange}>
                  <Option value="daily">Diario</Option>
                  <Option value="weekly">Semanal</Option>
                  <Option value="monthly">Mensual</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={16}>
              <Form.Item label="Rango de Fechas" name="dateRange">
                <RangePicker
                  style={{ width: '100%' }}
                  disabledDate={disabledDate}
                  onChange={handleDateRangeChange}
                  format="DD/MM/YYYY"
                  value={[dateRange.startDate, dateRange.endDate]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div style={{ 
          flex: 1, 
          overflowY: chartConfigs.length < 4 ? 'hidden' : 'auto',
          paddingBottom: '6em'
        }}>
          <Spin 
            spinning={loading} 
            style={{ 
              height: '100%',
              maxHeight: '100%'
            }}
            wrapperClassName="dashboard-spin-wrapper"
          >
            <div 
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
                gridAutoRows: 'minmax(400px, auto)',
                gap: '1em',
                minHeight: '100%',
                padding: '1px'
              }}
            >
              {chartConfigs.map((config, index) => (
                <div 
                  key={config.key}
                  style={{
                    minHeight: '400px',
                    height: 'auto'
                  }}
                >
                  <DashboardChart 
                    title={config.title}
                    datasets={config.datasets}
                    height="100%"
                  />
                </div>
              ))}
            </div>
          </Spin>
        </div>
      </Form>

      <style>{`
        :global(.dashboard-spin-wrapper) {
          height: 100% !important;
        }
        :global(.dashboard-spin-wrapper .ant-spin-container) {
          height: 100% !important;
        }
        :global(.dashboard-spin-wrapper .ant-spin-nested-loading) {
          height: 100% !important;
        }
      `}</style>
    </ContainerLayout>
  );
};

export default DashboardView;